// frontend/src/pages/CheckoutPage.jsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, ArrowRight, Loader2, ShoppingBag, Truck, Store, 
  Calendar, CheckCircle, ChevronDown, Search, X, Navigation,
  Home, Building, Map, Phone, User, Mail, Clock
} from 'lucide-react';
import { useCart } from '../hooks/useCart';
import { useAuthStore } from '../store/authStore';
import { orderService } from '../services/orderService';
import { toast } from 'react-hot-toast';
import config from '../config';

// کامپوننت نقشه (با Lazy Loading برای جلوگیری از خطا)
const MapComponent = ({ onLocationSelect, initialPosition }) => {
  const mapRef = useRef(null);
  const [position, setPosition] = useState(initialPosition || [35.6892, 51.3890]); // تهران پیش‌فرض
  const [markerPosition, setMarkerPosition] = useState(initialPosition || [35.6892, 51.3890]);
  const [address, setAddress] = useState('');

  useEffect(() => {
    // بارگذاری پویا Leaflet
    const loadLeaflet = async () => {
      try {
        const L = await import('leaflet');
        await import('leaflet/dist/leaflet.css');
        
        // رفع مشکل آیکون‌های Leaflet
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
        });

        if (!mapRef.current) {
          const map = L.map('map').setView(position, 13);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
          }).addTo(map);

          const marker = L.marker(position, { draggable: true }).addTo(map);
          
          marker.on('dragend', function(e) {
            const latlng = marker.getLatLng();
            setMarkerPosition([latlng.lat, latlng.lng]);
            reverseGeocode(latlng.lat, latlng.lng);
            onLocationSelect(latlng.lat, latlng.lng);
          });

          map.on('click', function(e) {
            const latlng = e.latlng;
            marker.setLatLng(latlng);
            setMarkerPosition([latlng.lat, latlng.lng]);
            reverseGeocode(latlng.lat, latlng.lng);
            onLocationSelect(latlng.lat, latlng.lng);
          });

          mapRef.current = { map, marker };
          
          // اگر موقعیت اولیه داشتیم، آدرس را دریافت کن
          if (initialPosition) {
            reverseGeocode(initialPosition[0], initialPosition[1]);
          }
        }
      } catch (error) {
        console.error('Error loading Leaflet:', error);
      }
    };

    loadLeaflet();

    return () => {
      if (mapRef.current) {
        mapRef.current.map.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=fa`
      );
      const data = await response.json();
      if (data.display_name) {
        setAddress(data.display_name);
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const latlng = [pos.coords.latitude, pos.coords.longitude];
          setPosition(latlng);
          setMarkerPosition(latlng);
          if (mapRef.current) {
            mapRef.current.map.setView(latlng, 15);
            mapRef.current.marker.setLatLng(latlng);
          }
          reverseGeocode(latlng[0], latlng[1]);
          onLocationSelect(latlng[0], latlng[1]);
          toast.success('موقعیت شما با موفقیت دریافت شد');
        },
        () => {
          toast.error('خطا در دریافت موقعیت. لطفاً دستی انتخاب کنید.');
        }
      );
    } else {
      toast.error('مرورگر شما از موقعیت‌یابی پشتیبانی نمی‌کند');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button
          onClick={getCurrentLocation}
          className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition text-sm"
        >
          <Navigation className="w-4 h-4" />
          دریافت موقعیت فعلی
        </button>
        {address && (
          <span className="text-xs text-gray-500 flex-1 truncate" title={address}>
            📍 {address}
          </span>
        )}
      </div>
      <div id="map" className="w-full h-64 md:h-80 rounded-lg border border-gray-200 z-0" />
      <p className="text-xs text-gray-400">روی نقشه کلیک کنید یا نشانگر را بکشید تا موقعیت دقیق را انتخاب کنید.</p>
    </div>
  );
};

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const cart = useCart();
  const { items, totalPrice, clearCart } = cart;
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  
  // اطلاعات آدرس
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [location, setLocation] = useState(null);
  const [locationAddress, setLocationAddress] = useState('');
  
  // نحوه تحویل
  const [deliveryMethod, setDeliveryMethod] = useState('shipping'); // 'shipping' | 'store'
  
  // اطلاعات تماس
  const [fullName, setFullName] = useState(user?.full_name || user?.username || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [email, setEmail] = useState(user?.email || '');

  // لیست استان‌ها و شهرها
  const provinces = [
    'تهران', 'اصفهان', 'فارس', 'خراسان رضوی', 'خوزستان', 
    'آذربایجان شرقی', 'آذربایجان غربی', 'مازندران', 'گیلان', 'کرمان',
    'کرمانشاه', 'همدان', 'قم', 'یزد', 'سمنان', 'زنجان'
  ];

  const citiesByProvince = {
    'تهران': ['تهران', 'شهر ری', 'ورامین', 'کرج', 'ملارد', 'قدس'],
    'اصفهان': ['اصفهان', 'کاشان', 'نجف آباد', 'خمینی شهر', 'شاهین شهر'],
    'فارس': ['شیراز', 'مرودشت', 'کازرون', 'لارستان', 'فسا'],
    'خراسان رضوی': ['مشهد', 'نیشابور', 'سبزوار', 'تربت حیدریه', 'قوچان'],
    'خوزستان': ['اهواز', 'آبادان', 'خرمشهر', 'دزفول', 'اندیمشک'],
    'آذربایجان شرقی': ['تبریز', 'مراغه', 'مرند', 'جلفا', 'بناب'],
    'آذربایجان غربی': ['ارومیه', 'خوی', 'بوکان', 'مهاباد', 'سلماس'],
    'مازندران': ['ساری', 'بابل', 'آمل', 'گرگان', 'نوشهر'],
    'گیلان': ['رشت', 'بندر انزلی', 'لاهیجان', 'آستارا', 'رودسر'],
    'کرمان': ['کرمان', 'رفسنجان', 'جیرفت', 'سیرجان', 'زرند'],
    'کرمانشاه': ['کرمانشاه', 'اسلام‌آباد غرب', 'سنقر', 'هرسین'],
    'همدان': ['همدان', 'ملایر', 'نهاوند', 'تویسرکان'],
    'قم': ['قم', 'جعفرآباد', 'دستجرد'],
    'یزد': ['یزد', 'میبد', 'اردکان', 'بافق', 'مهریز'],
    'سمنان': ['سمنان', 'شاهرود', 'دامغان', 'گرمسار'],
    'زنجان': ['زنجان', 'ابهر', 'خرمدره', 'ماهنشان'],
  };

  const getCities = () => {
    return citiesByProvince[province] || [];
  };

  const handleLocationSelect = (lat, lng) => {
    setLocation({ lat, lng });
  };

  const handleSubmit = async () => {
    // اعتبارسنجی
    if (deliveryMethod === 'shipping') {
      if (!province) {
        toast.error('لطفاً استان را انتخاب کنید');
        return;
      }
      if (!city) {
        toast.error('لطفاً شهر را انتخاب کنید');
        return;
      }
      if (!addressDetail) {
        toast.error('لطفاً آدرس کامل را وارد کنید');
        return;
      }
      if (!postalCode) {
        toast.error('لطفاً کد پستی را وارد کنید');
        return;
      }
    }

    if (!fullName.trim()) {
      toast.error('لطفاً نام و نام خانوادگی را وارد کنید');
      return;
    }
    if (!phone.trim()) {
      toast.error('لطفاً شماره تماس را وارد کنید');
      return;
    }

    setIsProcessing(true);
    try {
      const shippingAddress = deliveryMethod === 'shipping' 
        ? `${province}، ${city}، ${addressDetail} - کد پستی: ${postalCode}${location ? ` (مختصات: ${location.lat}, ${location.lng})` : ''}`
        : 'تحویل در فروشگاه - تهران، سهروردی';

      const orderData = {
        user_id: user?.id,
        items: items.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.final_price || item.price || 0
        })),
        shipping_address: shippingAddress,
        payment_method: 'online',
        is_manual_price: false,
        recipient_name: fullName,
        recipient_phone: phone,
        city: deliveryMethod === 'shipping' ? city : 'تهران',
        province: deliveryMethod === 'shipping' ? province : 'تهران',
        postal_code: deliveryMethod === 'shipping' ? postalCode : '',
        note: `نحوه تحویل: ${deliveryMethod === 'shipping' ? 'ارسال به آدرس' : 'تحویل در فروشگاه'}${location ? ` | موقعیت مکانی: ${location.lat}, ${location.lng}` : ''}`,
      };

      console.log('📦 Sending order data:', orderData);
      
      const response = await orderService.create(orderData);
      console.log('✅ Create response:', response.data);
      
      const orderId = response.data?.id;
      
      if (!orderId) {
        throw new Error('شناسه سفارش دریافت نشد');
      }

      clearCart();
      toast.success('سفارش با موفقیت ثبت شد! لطفاً روش پرداخت را انتخاب کنید.');
      navigate(`/orders/${orderId}`);
      
    } catch (error) {
      console.error('❌ Checkout error:', error);
      
      if (error.response?.data) {
        const errors = error.response.data;
        let errorMessage = 'خطا در ثبت سفارش: ';
        
        if (typeof errors === 'object') {
          const messages = [];
          Object.keys(errors).forEach(key => {
            if (Array.isArray(errors[key])) {
              messages.push(`${key}: ${errors[key].join(', ')}`);
            } else if (typeof errors[key] === 'string') {
              messages.push(`${key}: ${errors[key]}`);
            } else {
              messages.push(JSON.stringify(errors[key]));
            }
          });
          errorMessage += messages.join(' | ');
        } else {
          errorMessage += errors;
        }
        
        toast.error(errorMessage);
      } else {
        toast.error('خطا در ارتباط با سرور');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (!items || items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">سبد خرید خالی است</h2>
          <button 
            onClick={() => navigate('/products')} 
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700"
          >
            بازگشت به فروشگاه
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* فرم ثبت سفارش */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <MapPin className="w-6 h-6 text-blue-600" />
                  تکمیل اطلاعات سفارش
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                  اطلاعات مورد نیاز برای ثبت سفارش را وارد کنید
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* ========== اطلاعات تماس ========== */}
                <div>
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-600" />
                    اطلاعات تماس
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        نام و نام خانوادگی <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="مثال: علی محمدی"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        شماره تماس <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="مثال: 09123456789"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ایمیل
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="مثال: info@example.com"
                      />
                    </div>
                  </div>
                </div>

                {/* ========== نحوه تحویل ========== */}
                <div>
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-blue-600" />
                    نحوه تحویل
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                      className={`border-2 rounded-lg p-4 cursor-pointer transition ${
                        deliveryMethod === 'shipping'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setDeliveryMethod('shipping')}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          checked={deliveryMethod === 'shipping'}
                          onChange={() => setDeliveryMethod('shipping')}
                          className="w-4 h-4 text-blue-600"
                        />
                        <Truck className={`w-5 h-5 ${deliveryMethod === 'shipping' ? 'text-blue-600' : 'text-gray-400'}`} />
                        <div>
                          <span className="font-medium">ارسال به آدرس</span>
                          <p className="text-xs text-gray-500">هزینه ارسال به عهده خریدار می‌باشد</p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`border-2 rounded-lg p-4 cursor-pointer transition ${
                        deliveryMethod === 'store'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setDeliveryMethod('store')}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          checked={deliveryMethod === 'store'}
                          onChange={() => setDeliveryMethod('store')}
                          className="w-4 h-4 text-blue-600"
                        />
                        <Store className={`w-5 h-5 ${deliveryMethod === 'store' ? 'text-blue-600' : 'text-gray-400'}`} />
                        <div>
                          <span className="font-medium">تحویل در فروشگاه</span>
                          <p className="text-xs text-gray-500">تهران، سهروردی</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ========== آدرس (فقط در حالت ارسال) ========== */}
                {deliveryMethod === 'shipping' && (
                  <div>
                    <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-600" />
                      آدرس تحویل
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          استان <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={province}
                          onChange={(e) => {
                            setProvince(e.target.value);
                            setCity('');
                          }}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">انتخاب استان</option>
                          {provinces.map(p => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          شهر <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          disabled={!province}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                        >
                          <option value="">انتخاب شهر</option>
                          {getCities().map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          آدرس کامل <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={addressDetail}
                          onChange={(e) => setAddressDetail(e.target.value)}
                          rows={3}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="خیابان، پلاک، واحد، کد پستی..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          کد پستی <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="کد پستی ۱۰ رقمی"
                          maxLength={10}
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={() => setIsMapOpen(!isMapOpen)}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition text-sm"
                        >
                          <Map className="w-4 h-4" />
                          {isMapOpen ? 'بستن نقشه' : 'انتخاب از نقشه'}
                        </button>
                      </div>
                    </div>

                    {/* نقشه */}
                    {isMapOpen && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <MapComponent onLocationSelect={handleLocationSelect} />
                        {location && (
                          <div className="mt-2 text-xs text-gray-500">
                            📍 مختصات انتخاب شده: {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* دکمه‌ها */}
            <div className="flex justify-between mt-6">
              <button
                onClick={() => navigate('/cart')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition"
              >
                <ArrowRight className="w-4 h-4" />
                بازگشت به سبد خرید
              </button>
              <button
                onClick={handleSubmit}
                disabled={isProcessing}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-8 py-3 rounded-lg font-bold flex items-center gap-2 transition"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    در حال ثبت...
                  </>
                ) : (
                  <>
                    ثبت سفارش
                    <CheckCircle className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* ========== سایدبار: خلاصه سفارش ========== */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-blue-600" />
                خلاصه سفارش
              </h3>

              {/* تاریخ */}
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-4 pb-3 border-b border-gray-100">
                <Calendar className="w-4 h-4" />
                <span>تاریخ ثبت: {new Date().toLocaleDateString('fa-IR')}</span>
              </div>

              {/* محصولات */}
              <div className="space-y-3 mb-4 max-h-60 overflow-auto">
                {items.map((item, index) => (
                  <div key={item.id} className="flex items-start gap-2 text-sm p-2 bg-gray-50 rounded-lg">
                    <span className="text-xs text-gray-400 font-medium w-6">{index + 1}</span>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{item.name}</p>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{item.quantity} × {new Intl.NumberFormat('fa-IR').format(item.final_price || item.price)} تومان</span>
                        <span className="font-bold text-gray-700">
                          {new Intl.NumberFormat('fa-IR').format((item.final_price || item.price) * item.quantity)} تومان
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* جمع کل */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">جمع کل</span>
                  <span>{new Intl.NumberFormat('fa-IR').format(totalPrice)} تومان</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">هزینه ارسال</span>
                  <span className="text-green-600">رایگان</span>
                </div>
              </div>
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>مبلغ قابل پرداخت</span>
                  <span className="text-blue-600">
                    {new Intl.NumberFormat('fa-IR').format(totalPrice)} تومان
                  </span>
                </div>
              </div>

              {/* نحوه تحویل */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-500">
                <p className="flex items-center gap-2">
                  {deliveryMethod === 'shipping' ? (
                    <><Truck className="w-3 h-3" /> ارسال به آدرس</>
                  ) : (
                    <><Store className="w-3 h-3" /> تحویل در فروشگاه</>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};